import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import UploadFileModal from '@nxsys/components/common/UploadFileModal';
import {
  useImportEmployee,
  useImportXMLEmployee,
} from '@nxsys/hooks/api/import';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { importAction } from '@nxsys/store/slices/import/reducer/importSlice';
import { GetImportEmployeeListParams } from '@nxsys/store/slices/import/types';
import { debounce } from 'lodash';
import {
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ImportResultUploadModal from '../components/ImportResultUploadModal';
import ViewImportedEmployeeFile, {
  IMPORT_TYPE,
} from '../components/ViewImportedEmployeeFile';
import { getImportEmployeeColumns } from './columns';
import { IMPORT_SCREEN_TYPE } from '../constants';
import useImportSocket from '@nxsys/hooks/useImportSocket';
import { showMessage } from '@nxsys/utils/message';
import ImportXMLResultUploadModal from '../components/ImportXMLResultUploadModal';
import { IRELAND } from '@nxsys/store/constants/keys';

const ImportEmployeeTab = ({
  importFileRef,
}: {
  importFileRef: Ref<{ open: () => void }>;
}) => {
  const { messages } = useTypedIntl();

  const { user, isBureauEmployer, isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;

  const { importEmployeeList } = useSelector(
    (state: AppState) => state.importStore,
  );
  const { loading, list, pagination, queryParams } = importEmployeeList;

  const dispatch = useDispatch<AppDispatch>();

  const [searchValue, setSearchValue] = useState('');
  const [openUploadFileModal, setOpenUploadFileModal] = useState(false);
  const [openResultUploadModal, setOpenResultUploadModal] = useState(false);
  const [dataImported, setDataImported] = useState<IImportEmployee | null>(
    null,
  );
  const [openViewImportedFileModal, setOpenViewImportedFileModal] =
    useState(false);
  const [openXMLResultUploadModal, setOpenXMLResultUploadModal] =
    useState(false);

  useImperativeHandle(importFileRef, () => ({
    open: () => {
      setOpenUploadFileModal(true);
    },
  }));

  const { run: importEmployee, loading: importEmployeeLoading } =
    useImportEmployee();

  const { run: importXMLEmployee, loading: importXMLEmployeeLoading } =
    useImportXMLEmployee();

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
    });
  }, []);

  const handleFetchData = (params: GetImportEmployeeListParams) => {
    dispatch(
      importAction.getImportEmployeeList({
        authorId: isBureauEmployer ? user?.id : undefined,
        ...params,
      }),
    );
  };

  const refetchData = () =>
    handleFetchData({
      ...queryParams,
    });

  const debouncedFetchData = useCallback(
    debounce((search: string) => {
      handleFetchData({
        ...queryParams,
        page: 0,
        size: 20,
        search: search,
      });
    }, 500),
    [queryParams],
  );

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedFetchData(value);
  };

  const handleUploaded = useCallback(
    async (fileData: { url: string; fileName: string; fileSize: number }) => {
      const params = {
        ...fileData,
        isBureau: isBureau,
        isIreland: isIreland,
      };
      const fileExtension =
        '.' + fileData.fileName.split('.').pop()?.toLowerCase();
      if (fileExtension === '.xml') {
        setOpenXMLResultUploadModal(true);
        const res = await importXMLEmployee(params);
        setDataImported(res.data);
      } else {
        setOpenResultUploadModal(true);
        const res = await importEmployee(params);
        setDataImported(res.data);
      }
    },
    [],
  );

  const handleViewImportedFile = useCallback((data: IImportEmployee) => {
    setDataImported(data);
    setOpenViewImportedFileModal(true);
  }, []);

  useImportSocket({
    eventName: `import-process-notification-${user?.id}`,
    onSuccess: (data) => {
      if (data?.success) {
        showMessage({
          type: 'success',
          content: messages['import.employee.importInProgress'],
        });
        refetchData();
      }
    },
  });

  return (
    <>
      <div className="ImportEmployeeTab">
        <AppsHeader>
          <AppInputSearch value={searchValue} onChange={handleSearchChange} />
        </AppsHeader>
        <AppsContent>
          <AppTableContainer
            data={
              list?.map((item, index) => ({
                ...item,
                indexNumber:
                  pagination.current * pagination.pageSize + index + 1,
              })) ?? []
            }
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: 1000, y: 700 }}
            columns={getImportEmployeeColumns({
              messages,
              onView: handleViewImportedFile,
            })}
            onChangePagination={onChangePagination}
            noDataType={!!queryParams?.search ? 'noDataFound' : 'noDataCreated'}
            addNewBtnName={messages['common.importFile']}
            addNewBtnOnClick={() => setOpenUploadFileModal(true)}
            addNewIcon={null}
          />
        </AppsContent>
      </div>
      <UploadFileModal
        open={openUploadFileModal}
        onClose={() => setOpenUploadFileModal(false)}
        onUploaded={handleUploaded}
        fileType="spreadsheet,xml"
      />
      <ImportResultUploadModal
        isOpen={openResultUploadModal}
        toggle={() => setOpenResultUploadModal(!openResultUploadModal)}
        resultData={dataImported}
        loading={importEmployeeLoading}
        refetchData={refetchData}
        importScreenType={IMPORT_SCREEN_TYPE.EMPLOYEE}
      />
      <ViewImportedEmployeeFile
        isOpen={openViewImportedFileModal}
        toggle={() => setOpenViewImportedFileModal(!openViewImportedFileModal)}
        id={dataImported?.id}
        fileType={!!dataImported?.xmlType ? IMPORT_TYPE.XML : IMPORT_TYPE.EXCEL}
      />
      <ImportXMLResultUploadModal
        isOpen={openXMLResultUploadModal}
        toggle={() => setOpenXMLResultUploadModal(!openXMLResultUploadModal)}
        resultData={dataImported}
        loading={importXMLEmployeeLoading}
      />
    </>
  );
};

export default ImportEmployeeTab;
